import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

interface ContentFilterResult {
  isApproved: boolean;
  confidence: number;
  flaggedWords: string[];
  reason: string;
  requiresReview: boolean;
}

interface FlaggedContent {
  id?: string;
  userId: string;
  userName: string;
  content: string;
  contentType: 'comment' | 'post' | 'message';
  flaggedWords: string[];
  confidence: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  adminNotes?: string;
  createdAt: any;
  updatedAt: any;
}

class ContentFilterService {
  private negativeWords: Set<string>;
  private profanityWords: Set<string>;
  private toxicPatterns: RegExp[];

  constructor() {
    // Nederlandse scheldwoorden en negatieve woorden
    this.profanityWords = new Set([
      'kut', 'klote', 'tyfus', 'kanker', 'hoer', 'slet', 'neuk', 'pik', 'lul', 'kloot',
      'shit', 'fuck', 'bitch', 'asshole', 'dick', 'pussy', 'bastard', 'motherfucker',
      'godverdomme', 'godver', 'verdomme', 'verdomd', 'kutwijf', 'klootzak', 'eikel',
      'debiel', 'idioot', 'mongool', 'retard', 'spast', 'gehandicapt', 'achterlijk'
    ]);

    // Negatieve woorden die context nodig hebben
    this.negativeWords = new Set([
      'hate', 'haat', 'verafschuw', 'verafschuwen', 'walg', 'walgen', 'stom', 'dom',
      'slecht', 'rot', 'kut', 'klote', 'sukkel', 'loser', 'verliezer', 'nietsnut',
      'waardeloos', 'nutteloos', 'onzin', 'bullshit', 'onzin', 'domme', 'stomme',
      'slechte', 'rotte', 'kut', 'klote', 'sukkel', 'loser', 'verliezer', 'nietsnut'
    ]);

    // Toxische patronen
    this.toxicPatterns = [
      /\b(ik haat|i hate)\b/gi,
      /\b(verafschuw|verafschuwen)\b/gi,
      /\b(walg van|walg van)\b/gi,
      /\b(stomme|domme)\s+\w+/gi,
      /\b(rot|kut|klote)\s+\w+/gi,
      /\b(sukkel|loser|verliezer)\b/gi,
      /\b(nietsnut|waardeloos|nutteloos)\b/gi,
      /\b(bullshit|onzin)\b/gi,
      /\b(ga dood|sterf|pleeg zelfmoord)\b/gi,
      /\b(ik hoop dat je|i hope you)\s+\w+\s+(doodgaat|sterft)\b/gi,
      /\b(je bent|you are)\s+(dom|stom|slecht|rot)\b/gi,
      /\b(ik wens dat|i wish)\s+\w+\s+(doodgaat|sterft)\b/gi
    ];
  }

  // Analyseer content op negativiteit
  async analyzeContent(content: string, userId: string, userName: string, contentType: 'comment' | 'post' | 'message'): Promise<ContentFilterResult> {
    const lowerContent = content.toLowerCase();
    const words = lowerContent.split(/\s+/);
    
    let flaggedWords: string[] = [];
    let confidence = 0;
    let reason = '';

    // Check voor scheldwoorden
    const foundProfanity = words.filter(word => this.profanityWords.has(word));
    if (foundProfanity.length > 0) {
      flaggedWords.push(...foundProfanity);
      confidence += foundProfanity.length * 0.3;
      reason += `Scheldwoorden gedetecteerd: ${foundProfanity.join(', ')}. `;
    }

    // Check voor negatieve woorden
    const foundNegative = words.filter(word => this.negativeWords.has(word));
    if (foundNegative.length > 0) {
      flaggedWords.push(...foundNegative);
      confidence += foundNegative.length * 0.2;
      reason += `Negatieve woorden gedetecteerd: ${foundNegative.join(', ')}. `;
    }

    // Check voor toxische patronen
    let patternMatches = 0;
    this.toxicPatterns.forEach(pattern => {
      const matches = lowerContent.match(pattern);
      if (matches) {
        patternMatches += matches.length;
        confidence += matches.length * 0.4;
        reason += `Toxisch patroon gedetecteerd: "${matches[0]}". `;
      }
    });

    // Check voor ALL CAPS (agressie indicator)
    const capsRatio = (content.match(/[A-Z]/g) || []).length / content.length;
    if (capsRatio > 0.7 && content.length > 10) {
      confidence += 0.2;
      reason += 'Overmatig gebruik van hoofdletters (agressie indicator). ';
    }

    // Check voor herhaalde leestekens
    const repeatedPunctuation = content.match(/[!?]{3,}/g);
    if (repeatedPunctuation) {
      confidence += repeatedPunctuation.length * 0.1;
      reason += 'Overmatig gebruik van leestekens. ';
    }

    // Check voor persoonlijke aanvallen
    const personalAttackPatterns = [
      /\b(je bent|you are)\s+\w+/gi,
      /\b(jij bent|you are)\s+\w+/gi,
      /\b(ga dood|sterf)\b/gi,
      /\b(ik haat jou|i hate you)\b/gi
    ];

    personalAttackPatterns.forEach(pattern => {
      const matches = lowerContent.match(pattern);
      if (matches) {
        confidence += matches.length * 0.5;
        reason += `Persoonlijke aanval gedetecteerd: "${matches[0]}". `;
      }
    });

    // Bepaal of content goedgekeurd wordt
    const isApproved = confidence < 0.5;
    const requiresReview = confidence >= 0.3 && confidence < 0.8;

    // Als content wordt geflagged, sla op voor admin review
    if (!isApproved || requiresReview) {
      await this.flagContentForReview({
        userId,
        userName,
        content,
        contentType,
        flaggedWords,
        confidence,
        reason,
        status: requiresReview ? 'pending' : 'rejected'
      });
    }

    return {
      isApproved,
      confidence: Math.min(confidence, 1.0),
      flaggedWords,
      reason: reason.trim() || 'Geen specifieke reden',
      requiresReview
    };
  }

  // Flag content voor admin review
  private async flagContentForReview(flaggedContent: Omit<FlaggedContent, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
    try {
      await addDoc(collection(db, 'flaggedContent'), {
        ...flaggedContent,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error flagging content for review:', error);
    }
  }

  // AI-powered sentiment analyse (simulatie)
  private async analyzeSentiment(content: string): Promise<number> {
    // Simuleer AI sentiment analyse
    // In productie zou je hier een echte AI service gebruiken zoals OpenAI, Google NLP, etc.
    
    const positiveWords = ['goed', 'leuk', 'mooi', 'geweldig', 'fantastisch', 'super', 'top', 'uitstekend', 'perfect'];
    const negativeWords = ['slecht', 'rot', 'dom', 'stom', 'kut', 'klote', 'hate', 'haat', 'walg'];
    
    const words = content.toLowerCase().split(/\s+/);
    let sentiment = 0;
    
    words.forEach(word => {
      if (positiveWords.includes(word)) sentiment += 0.1;
      if (negativeWords.includes(word)) sentiment -= 0.2;
    });
    
    return Math.max(-1, Math.min(1, sentiment));
  }

  // Check voor spam
  private detectSpam(content: string): boolean {
    const spamPatterns = [
      /\b(buy now|koop nu|actie|sale|discount|korting)\b/gi,
      /\b(click here|klik hier|visit|bezoek)\s+\w+\.(com|nl|be)\b/gi,
      /\b(free|gratis|gratis)\s+\w+\b/gi,
      /\b(money|geld|verdien|earn)\b/gi,
      /\b(bitcoin|crypto|investment|investering)\b/gi
    ];

    return spamPatterns.some(pattern => pattern.test(content));
  }

  // Volledige content analyse
  async fullContentAnalysis(content: string, userId: string, userName: string, contentType: 'comment' | 'post' | 'message'): Promise<ContentFilterResult> {
    // Check voor spam eerst
    if (this.detectSpam(content)) {
      return {
        isApproved: false,
        confidence: 0.9,
        flaggedWords: ['spam'],
        reason: 'Spam gedetecteerd',
        requiresReview: false
      };
    }

    // Normale content analyse
    const result = await this.analyzeContent(content, userId, userName, contentType);
    
    // Extra AI sentiment check voor borderline gevallen
    if (result.confidence >= 0.3 && result.confidence <= 0.6) {
      const sentiment = await this.analyzeSentiment(content);
      if (sentiment < -0.3) {
        result.isApproved = false;
        result.confidence = Math.min(result.confidence + 0.2, 1.0);
        result.reason += ' Negatieve sentiment gedetecteerd door AI.';
      }
    }

    return result;
  }

  // Admin functies
  async approveContent(flaggedContentId: string): Promise<void> {
    // Implementeer admin approval logica
  }

  async rejectContent(flaggedContentId: string, adminNotes?: string): Promise<void> {
    // Implementeer admin rejection logica
  }

  async getFlaggedContent(): Promise<FlaggedContent[]> {
    // Implementeer ophalen van geflagged content voor admin
    return [];
  }
}

export default new ContentFilterService();















